import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, ShoppingBag, Plug } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import SEO from "@/components/SEO";
import PageHeader, { SectionMark } from "@/components/PageHeader";
import ProductCard from "@/components/shop/ProductCard";

export function useCatalog() {
    const [products, setProducts] = useState(null);
    const [status, setStatus] = useState(null);
    const load = () => Promise.all([api.get("/shop/catalog"), api.get("/shop/status")])
        .then(([c, s]) => { setProducts(c.data || []); setStatus(s.data); })
        .catch(() => { setProducts([]); setStatus(null); });
    useEffect(() => { load(); }, []);
    return { products, status, reload: load };
}

export default function Shop() {
    const { user } = useAuth();
    const { lang } = useLang();
    const en = lang === "en";
    const { products, status, reload } = useCatalog();
    const { setOpen, count } = useCart();
    const [type, setType] = useState("");
    const [syncing, setSyncing] = useState(false);
    const isAdmin = user && user.role === "admin";

    const OTHER = en ? "Other" : "Altro";
    const typeOf = (p) => p.productType || OTHER;
    const types = useMemo(() => [...new Set((products || []).map(typeOf))], [products, OTHER]);  // eslint-disable-line react-hooks/exhaustive-deps
    const collections = useMemo(() => {
        const m = new Map();
        (products || []).forEach((p) => (p.collections || []).forEach((c) => m.set(c.handle, c.title)));
        return [...m.entries()].map(([handle, title]) => ({ handle, title }));
    }, [products]);
    const visible = useMemo(() => (products || []).filter((p) => !type || typeOf(p) === type || (p.collections || []).some((c) => `c:${c.handle}` === type)), [products, type, OTHER]);  // eslint-disable-line react-hooks/exhaustive-deps

    const sync = async () => {
        setSyncing(true);
        try { const { data } = await api.post("/admin/shop/sync"); toast.success(`Sync OK · ${data.count} prodotti`); reload(); }
        catch (e) { toast.error(e.response?.data?.detail || "Sync fallita"); }
        finally { setSyncing(false); }
    };

    return (
        <div data-testid="shop-page" className="bg-abnormal-ink">
            <SEO title="Shop · Wear the thought" path="/shop"
                description={en ? "Official ABnormal shop: tees, hoodies, caps. Sizes, colours and secure Shopify checkout, right here." : "Shop ufficiale ABnormal: t-shirt, felpe, cappelli. Taglie, colori e checkout sicuro Shopify, direttamente qui."} />
            <PageHeader
                label="ABNORMAL · SHOP"
                title="Wear the"
                accent="thought"
                tagline={[en ? "Official shop" : "Shop ufficiale", en ? "Secure checkout" : "Checkout sicuro", "Printful"]}
                sub={en
                    ? "Everything ABnormal, bought right here: pick size and colour, add to cart, pay on Shopify's secure checkout. Produced and shipped by Printful."
                    : "Tutto ABnormal, si compra qui: scegli taglia e colore, aggiungi al carrello, paghi sul checkout sicuro Shopify. Prodotto e spedito da Printful."}
                right={
                    <button type="button" onClick={() => setOpen(true)} data-testid="shop-open-cart" className="btn-primary">
                        <ShoppingBag size={14} /> {en ? "Cart" : "Carrello"}{count > 0 ? ` (${count})` : ""}
                    </button>
                }
            />

            <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 md:py-14">
                {isAdmin && (
                    <div data-testid="shop-admin-bar" className="mb-8 border border-abnormal-acid/30 p-3">
                        <div className="flex flex-wrap gap-3 items-center justify-between">
                            <p className="label-mono text-abnormal-acid inline-flex items-center gap-2">
                                <Plug size={12} /> SHOPIFY · {status?.configured ? status.domain : "NON CONFIGURATO"}
                                {status?.last_sync && <span className="text-abnormal-paperDim">· sync {new Date(status.last_sync).toLocaleString("it-IT", { timeZone: "Europe/Rome" })} · {status.count} prodotti</span>}
                                {status?.error && <span className="text-abnormal-red">· {status.error}</span>}
                            </p>
                            <button type="button" onClick={sync} disabled={syncing || !status?.configured} data-testid="shop-sync-btn" className="btn-ghost !py-2 !px-3 disabled:opacity-40">
                                <RefreshCw size={13} className={syncing ? "animate-spin" : ""} /> Sync Shopify
                            </button>
                        </div>
                        {status?.not_on_channel?.length > 0 && (
                            <div data-testid="shop-channel-warning" className="mt-3 border-t border-abnormal-acid/20 pt-3 text-sm text-abnormal-paperDim">
                                <p className="text-abnormal-red label-mono">
                                    {status.not_on_channel.length} {status.not_on_channel.length === 1 ? "prodotto" : "prodotti"} non pubblicat{status.not_on_channel.length === 1 ? "o" : "i"} sul canale Storefront: in vendita tramite pagina Shopify, non col carrello del sito
                                </p>
                                <p className="mt-1">
                                    {status.not_on_channel.map((p) => p.title).join(" · ")}
                                </p>
                                <p className="mt-1 text-xs">
                                    Per il carrello nativo: Shopify → Prodotti → seleziona → "Gestisci canali di vendita" → spunta il canale dell'app Storefront (Headless). Il sito si aggiorna da solo entro 5 minuti.
                                </p>
                            </div>
                        )}
                    </div>
                )}

                <SectionMark n="01" title={en ? "Catalogue" : "Catalogo"}
                    right={<span className="label-mono text-abnormal-paperDim/60">{visible.length} {en ? "items" : "pezzi"}</span>} />

                {types.length >= 1 && (products || []).length > 1 && (
                    <div data-testid="shop-filters" className="mb-6 flex flex-wrap gap-2">
                        <button type="button" onClick={() => setType("")} data-testid="shop-filter-all" className={`label-mono px-3 py-2 border transition-colors ${!type ? "bg-abnormal-acid text-black border-abnormal-acid" : "border-white/15 hover:border-abnormal-acid"}`}>{en ? "All" : "Tutto"}</button>
                        {types.map((tp) => (
                            <button key={tp} type="button" onClick={() => setType(tp)} data-testid={`shop-filter-${tp}`} className={`label-mono px-3 py-2 border transition-colors ${type === tp ? "bg-abnormal-acid text-black border-abnormal-acid" : "border-white/15 hover:border-abnormal-acid"}`}>{tp}</button>
                        ))}
                        {collections.map((c) => (
                            <button key={c.handle} type="button" onClick={() => setType(`c:${c.handle}`)} data-testid={`shop-filter-collection-${c.handle}`} className={`label-mono px-3 py-2 border transition-colors ${type === `c:${c.handle}` ? "bg-abnormal-acid text-black border-abnormal-acid" : "border-white/15 hover:border-abnormal-acid/60"}`}>{c.title}</button>
                        ))}
                    </div>
                )}

                {products === null ? (
                    <div className="h-40" />
                ) : visible.length === 0 ? (
                    <div data-testid="shop-empty" className="border border-white/15 bg-abnormal-charcoal p-8 md:p-12 halftone">
                        <p className="font-display uppercase leading-[0.9] tracking-tight text-abnormal-paper text-3xl sm:text-5xl md:text-6xl">
                            {en ? "Shop loading." : "Shop in arrivo."}<br /><span className="text-abnormal-acid">Stay Abnormal.</span>
                        </p>
                        <p className="mt-4 text-abnormal-paperDim text-sm md:text-base max-w-xl">
                            {en ? "The catalogue is being connected to our Shopify store. Check back shortly." : "Il catalogo si sta collegando al nostro negozio Shopify. Torna tra poco."}
                        </p>
                    </div>
                ) : (
                    <div data-testid="product-grid" className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
                        {visible.map((p, i) => <ProductCard key={p.handle} product={p} index={i} />)}
                    </div>
                )}

                <div data-testid="shop-info-band" className="mt-14 border border-white/15 halftone p-6 md:p-10 grid md:grid-cols-3 gap-6">
                    {[
                        [en ? "Secure checkout" : "Checkout sicuro", en ? "Payments handled by Shopify. Cards, PayPal, Apple Pay & Google Pay where available." : "Pagamenti gestiti da Shopify. Carte, PayPal, Apple Pay e Google Pay dove disponibili."],
                        [en ? "Made to order" : "Stampato su richiesta", en ? "Produced and shipped by Printful. Less waste, more thought." : "Prodotto e spedito da Printful. Meno sprechi, più pensiero."],
                        [en ? "One world" : "Un solo mondo", en ? "Story, Murph and the shop live together here on abnormalworld.it." : "Story, Murph e lo shop vivono insieme qui su abnormalworld.it."],
                    ].map(([h, p]) => (
                        <div key={h}><p className="label-mono text-abnormal-acid">// {h}</p><p className="mt-2 text-sm text-abnormal-paperDim leading-relaxed">{p}</p></div>
                    ))}
                </div>
                <p className="mt-6 label-mono text-abnormal-paperDim/50"><Link to="/abnormal" className="hover:text-abnormal-acid">← ABNORMAL · Story · Murph</Link></p>
            </div>
        </div>
    );
}
